import _ from "lodash";
import { Calendar, Circle, Eye, Navigation, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Col } from "../../../../../components/col";
import { Input } from "../../../../../components/input";
import { Row } from "../../../../../components/row";
import CloseCircle from "../../../../../assets/icons/close-circle.svg";
import ActivityLevelPanel from "../../ActivityLevelPanel";
import EventDetailPanel from "../../EventDetailPanel";
import LiveEventsMap from "../../map/live-events/LiveEventsMap";
import { MapBodySkeleton, SidePanelSkeleton } from "../../region/region-skeletons";
import { RegionState } from "../../region/RegionState";
import { EventStatus } from "../../../types/Filter";
import type { OrganizationMapConfig } from "../../../commons/Constant";
import type {
  ActivityLevelModel,
  EventDetailModel,
  LiveEventAreaModel,
  LiveEventModel,
} from "../../../types/LiveEvent";
import { matchesLiveEventStatusFilter } from "../../../utils/live-event-status";

type LiveEventsTabProps = {
  areas: LiveEventAreaModel[] | null;
  organizationName: string;
  mapConfig?: OrganizationMapConfig;
  activityLevels: ActivityLevelModel[] | null;
  eventDetails: EventDetailModel[] | null;
  eventStatus?: EventStatus;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  filterRevision: number;
  onSearchChange: (value: string) => void;
};

const eventStatusLegend: ReadonlyArray<{
  color: string;
  label: string;
  status: EventStatus;
}> = [
  { color: "#00C853", label: "Active", status: EventStatus.Active },
  { color: "#00BCD4", label: "Starting Soon", status: EventStatus.StartingSoon },
  { color: "#FF9800", label: "Ending Soon", status: EventStatus.EndingSoon },
];

export default function LiveEventsTab({
  areas,
  organizationName,
  mapConfig,
  activityLevels,
  eventDetails,
  eventStatus,
  isLoading = false,
  error,
  onRetry,
  filterRevision,
  onSearchChange,
}: LiveEventsTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<LiveEventModel[] | null>(null);
  const debouncedSearch = useMemo(() => _.debounce(onSearchChange, 500), [onSearchChange]);
  const eventStatusCounts = useMemo(() => {
    const now = new Date();

    const counts: Partial<Record<EventStatus, number>> = {
      [EventStatus.Active]:
        eventDetails?.filter((event) =>
          matchesLiveEventStatusFilter(event, EventStatus.Active, now),
        ).length ?? 0,
      [EventStatus.StartingSoon]:
        eventDetails?.filter((event) =>
          matchesLiveEventStatusFilter(event, EventStatus.StartingSoon, now),
        ).length ?? 0,
      [EventStatus.EndingSoon]:
        eventDetails?.filter((event) =>
          matchesLiveEventStatusFilter(event, EventStatus.EndingSoon, now),
        ).length ?? 0,
    };

    return counts;
  }, [eventDetails]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [debouncedSearch, searchQuery]);
  useEffect(() => {
    setSelectedNeighborhood(null);
  }, [filterRevision]);

  return (
    <Row className="h-[600px] items-stretch">
      <div className="w-9/12 border border-gray-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-800 flex flex-col">
        <Row className="px-5 pt-5 justify-between items-center">
          <Col className="gap-1 text-sm">
            <div className="flex">
              <Navigation
                className="mr-1"
                color="#00C49A"
              />
              <span className="text-gray-900 dark:text-gray-100">
                {organizationName} - Live Events Heatmap
              </span>
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              Event activity across city districts - visual overview of citizen engagement
            </div>
          </Col>
        </Row>
        <Row className="px-5 pt-[15px] pb-[4px] justify-between items-center">
          <Col>
            <div className="w-80">
              <Input
                type="text"
                startIcon={<Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />}
                placeholder="Search events on map...."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="h-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100"
              />
            </div>
          </Col>
        </Row>
        <RegionState
          isLoading={isLoading}
          error={error}
          onRetry={onRetry}
          skeleton={<MapBodySkeleton />}
        >
          <div className="relative flex-1">
            <div className="absolute top-6 right-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 rounded-2xl px-4 py-3 text-[10px] z-[9]">
              <div className="font-semibold mb-2 flex items-center text-gray-900 dark:text-gray-100">
                <Eye
                  size={12}
                  className="mr-1"
                />
                <span>All Events</span>
              </div>
              <div className="ml-2 mb-2 flex items-center text-gray-700 dark:text-gray-300">
                <Calendar
                  size={12}
                  className="mr-2"
                />
                <span>{eventDetails?.length || 0} events</span>
              </div>
              <div className="mt-2 mb-1 text-gray-700 dark:text-gray-300">Event Status:</div>
              {eventStatusLegend.map(({ color, label, status }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 mb-1 text-gray-700 dark:text-gray-300"
                >
                  <Circle
                    size={16}
                    fill={color}
                    stroke="white"
                  />
                  <span>
                    {label} ({eventStatusCounts[status] ?? 0})
                  </span>
                </div>
              ))}
              <hr className="my-2 border-gray-200 dark:border-gray-600" />
              <span className="text-gray-500 dark:text-gray-400">Click events for details</span>
            </div>
            <div className="absolute bottom-6 left-6 bg-white dark:bg-gray-800 shadow-md border-2 border-gray-200 dark:border-gray-600 rounded-xl px-2 py-1 text-[10px] z-[2]">
              <div className="flex justify-left items-center">
                <Circle
                  className="mr-2"
                  size={12}
                  fill="#00BCD4"
                  stroke="#00BCD4"
                />
                <div>
                  <span className="text-[#00BCD4] dark:text-cyan-400 text-sm block">
                    {organizationName}
                  </span>
                  <span className="block text-gray-700 dark:text-gray-300">
                    Shelby County, Tennessee
                  </span>
                </div>
              </div>
            </div>
            <LiveEventsMap
              areas={areas}
              mapConfig={mapConfig}
              eventStatus={eventStatus}
              filterRevision={filterRevision}
              selectedNeighborhood={selectedNeighborhood}
              onSelectNeighborhood={setSelectedNeighborhood}
              onSelectEvent={(event) => {
                if (event) setSelectedNeighborhood([event]);
              }}
            />
          </div>
        </RegionState>
      </div>
      <RegionState
        isLoading={isLoading}
        error={error}
        onRetry={onRetry}
        skeleton={<SidePanelSkeleton />}
      >
        {_.isNil(selectedNeighborhood) ? (
          <ActivityLevelPanel data={activityLevels} />
        ) : (
          <Row className="border border-gray-200 dark:border-gray-700 rounded-2xl flex flex-col bg-white dark:bg-gray-800 px-4 w-3/12">
            <p className="w-full text-gray-900 dark:text-gray-100 pt-5 flex justify-between">
              <span>Event list</span>
              <button
                type="button"
                aria-label="Close event list"
                className="hover:cursor-pointer"
                onClick={() => setSelectedNeighborhood(null)}
              >
                <img
                  src={CloseCircle}
                  alt="close cỉcle"
                />
              </button>
            </p>
            <EventDetailPanel selectedNeighborhood={selectedNeighborhood} />
          </Row>
        )}
      </RegionState>
    </Row>
  );
}
